import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined'
import Check from '@mui/icons-material/Check'
import Close from '@mui/icons-material/Close'
import DeleteForeverOutlined from '@mui/icons-material/DeleteForeverOutlined'
import FastForwardOutlined from '@mui/icons-material/FastForwardOutlined'
import PlayCircleOutlineOutlined from '@mui/icons-material/PlayCircleOutlineOutlined'
import Avatar from '@mui/material/Avatar'
import Badge from '@mui/material/Badge'
import { grey, red, amber, blue, deepPurple, green } from '@mui/material/colors'
import Stack from '@mui/material/Stack'
import SvgIcon from '@mui/material/SvgIcon'
import Tooltip from '@mui/material/Tooltip'
import React from 'react'

import { ReactComponent as HoldIcon } from 'assets/work-orders/HoldIcon.svg'
import SkipReasonTooltip from 'components/work-order/SkipReasonTooltip'
import { getWorkOrderStatusIcon, hasSplitStatusIcon } from 'components/work-order/workOrderStatusIcon'
import { WORK_ORDER_STATUS } from 'settings/constants/service'
import T from 'T'
import { resolveDisplayedStatus } from 'utils/workOrderStatus'

import type { SxProps, Theme } from '@mui/material/styles'

const { SCHEDULED, IN_PROCESS, IN_PROGRESS, CANCELED, COMPLETE, SKIPPED, HOLD, VACATION_HOLD, SUSPENDED, SERVICED } = WORK_ORDER_STATUS
const SIDE_COMPLETION_COLOR: Record<string, string> = { [SERVICED]: green[500], [SKIPPED]: amber[500], [IN_PROCESS]: blue[500] }
const SIDE_COMPLETION_ICON_SX = { width: 8, height: 8, color: 'common.white' }
type Props = {
  status?: string
  intentStatus?: string | null
  sx?: SxProps<Theme>
  // A stop-level flag, not a work-order status: when set it wins over the status badge.
  isSuspended?: boolean
  // Lets a Skipped avatar show why it was skipped on hover.
  workOrderId?: string | null
}

const WorkOrderStatusAvatar: React.FC<Props> = ({
  status: rawStatus,
  intentStatus,
  sx,
  isSuspended: isStopSuspended = false,
  workOrderId,
}) => {
  const { status, completion, sideIntent, sideCompletion } = resolveDisplayedStatus(rawStatus, intentStatus)
  const isSuspended = isStopSuspended || status === SUSPENDED
  const isVacationHold = status === VACATION_HOLD
  const statusTitle =
    sideIntent || sideCompletion ? `${T.USER_INTENT_STATUS}: ${sideIntent || status} / ${T.COMPLETION_STATUS}: ${completion}` : status
  const isScheduled = status === SCHEDULED
  const isCompleted = status === COMPLETE
  const isCanceled = status === CANCELED
  const isSkipped = status === SKIPPED
  const isServiced = status === SERVICED
  const isMixed = hasSplitStatusIcon(status)
  const isInProcess = status === IN_PROCESS
  const isInProgress = status === IN_PROGRESS
  const isOnHold = status === HOLD
  const iconSx = { width: 14, height: 14, color: 'common.white' }
  const avatar = (
    <Badge
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      badgeContent={
        <Avatar
          sx={{
            width: 12,
            height: 12,
            border: '1.5px solid',
            borderColor: 'background.paper',
            bgcolor: hasSplitStatusIcon(sideCompletion) ? 'background.paper' : SIDE_COMPLETION_COLOR[sideCompletion] ?? grey[500],
          }}
        >
          {getWorkOrderStatusIcon(
            sideCompletion,
            hasSplitStatusIcon(sideCompletion) ? { width: '100%', height: '100%' } : SIDE_COMPLETION_ICON_SX
          )}
        </Avatar>
      }
      invisible={!sideCompletion || isStopSuspended}
      overlap="circular"
      sx={{ '& .MuiBadge-badge': { p: 0, minWidth: 0, height: 'auto', right: 2, bottom: 2 } }}
    >
      <Avatar
        sx={{
          width: 20,
          height: 20,
          ...(isScheduled && { bgcolor: grey[500] }),
          ...(isCanceled && { bgcolor: red[500] }),
          ...(isSkipped && { bgcolor: amber[500] }),
          ...((isInProcess || isInProgress) && { bgcolor: blue[500] }),
          ...((isOnHold || isVacationHold) && { bgcolor: deepPurple[500] }),
          ...((isCompleted || isServiced) && { bgcolor: green[500] }),
          ...(isMixed && { bgcolor: 'transparent' }),
          ...(isSuspended && { bgcolor: red[500] }),
          ...sx,
        }}
      >
        <Stack alignItems="center" height="100%" justifyContent="center" width="100%">
          {isSuspended ? (
            <DeleteForeverOutlined sx={iconSx} />
          ) : (
            <>
              {isCanceled && <Close sx={iconSx} />}
              {(isServiced || isCompleted) && <Check sx={iconSx} />}
              {isSkipped && <FastForwardOutlined sx={iconSx} />}
              {(isInProcess || isInProgress) && <PlayCircleOutlineOutlined sx={iconSx} />}
              {isOnHold && (
                <SvgIcon inheritViewBox sx={iconSx} viewBox="0 0 12 10">
                  <HoldIcon />
                </SvgIcon>
              )}
              {isVacationHold && getWorkOrderStatusIcon(status, iconSx)}
              {isScheduled && <CalendarTodayOutlined sx={iconSx} />}
              {isMixed && getWorkOrderStatusIcon(status, { width: '100%', height: '100%' })}
            </>
          )}
        </Stack>
      </Avatar>
    </Badge>
  )

  if (isSkipped && !isSuspended && workOrderId) {
    return (
      <SkipReasonTooltip header={statusTitle} workOrderId={workOrderId}>
        {avatar}
      </SkipReasonTooltip>
    )
  }
  return (
    <Tooltip arrow title={isSuspended ? T.SUSPENDED : statusTitle}>
      {avatar}
    </Tooltip>
  )
}

export default WorkOrderStatusAvatar
